/**
 * Conversation Service
 *
 * Handles API calls for conversations
 */

import { ConversationFilters } from "@/types/centralizedChat";
import { ConversationServiceResult } from "./types";

/**
 * Fetch conversations with filters
 */
export async function fetchConversations(
  filters: ConversationFilters = {}
): Promise<ConversationServiceResult> {
  const params = new URLSearchParams();
  if (filters.unreadOnly) params.append("unreadOnly", "true");
  if (filters.searchQuery) params.append("searchQuery", filters.searchQuery);
  if (filters.status?.length) params.append("status", filters.status.join(","));
  if (filters.manufacturerCompany)
    params.append("manufacturerCompany", filters.manufacturerCompany);

  const response = await fetch(`/api/conversations?${params.toString()}`, {
    credentials: "include",
    headers: {
      "Cache-Control": "max-age=30, must-revalidate",
    },
  });

  // Handle 401 - session refresh is handled by middleware automatically
  // No need to manually refresh session

  if (!response.ok) {
    const errorMessage =
      response.status === 401
        ? "Your session may have expired. Please sign in again."
        : "Unable to load conversations. Please try again.";
    throw new Error(errorMessage);
  }

  const responseData = await response.json();
  const conversations = responseData.data.conversations || [];

  return {
    conversations,
    timestamp: Date.now(),
  };
}

/**
 * Create or get existing conversation with manufacturer
 */
export async function createOrGetConversation(
  manufacturerCompanyId: string
): Promise<string> {
  const response = await fetch("/api/conversations", {
    method: "POST",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ manufacturerCompanyId }),
  });

  if (!response.ok) {
    throw new Error("Failed to create conversation");
  }

  const data = await response.json();
  const conversationId = data.data?.conversationId;

  if (!conversationId) {
    throw new Error("Conversation ID not returned");
  }

  return conversationId;
}
